import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Eye, Check, XCircle, Ban, Trash2, ShieldCheck, ShieldOff, AlertTriangle,
} from "lucide-react";
import { profileMainImage, onImageError } from "@/lib/img";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_TONE = {
  active:    "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  pending:   "bg-amber-500/10   text-amber-300   border-amber-500/30",
  suspended: "bg-rose-500/10    text-rose-300    border-rose-500/30",
  rejected:  "bg-rose-500/10    text-rose-300    border-rose-500/30",
};

const VERIF_TONE = {
  verified:     "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  pending:      "bg-amber-500/10   text-amber-300   border-amber-500/30",
  rejected:     "bg-rose-500/10    text-rose-300    border-rose-500/30",
  not_verified: "bg-stone-800/60   text-stone-300   border-stone-700",
};

export default function AdminProfiles() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [profiles, setProfiles] = useState([]);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/admin/profiles");
      setProfiles(Array.isArray(data.profiles) ? data.profiles : []);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError({ status, detail: typeof detail === "string" ? detail : String(detail || e.message || "Failed to load profiles") });
      // eslint-disable-next-line no-console
      console.warn("[AdminProfiles] load:", status, detail);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return profiles;
    return profiles.filter(p =>
      (p.name || "").toLowerCase().includes(s) ||
      (p.city || "").toLowerCase().includes(s) ||
      (p.id || "").toLowerCase().includes(s)
    );
  }, [profiles, q]);

  const setStatus = async (pid, status) => {
    setBusyId(pid);
    try {
      await api.post(`/admin/profiles/${pid}/status`, { status });
      toast.success(`Profile ${status}`);
      load();
    } catch (e) {
      console.warn("[AdminProfiles] setStatus:", e);
      toast.error("Update failed");
    } finally { setBusyId(null); }
  };

  const removeProfile = async (pid) => {
    if (!window.confirm("Permanently delete this profile? This cannot be undone.")) return;
    setBusyId(pid);
    try {
      await api.delete(`/admin/profiles/${pid}`);
      toast.success("Profile deleted");
      setProfiles((ps) => ps.filter(p => p.id !== pid));
    } catch (e) {
      console.warn("[AdminProfiles] delete:", e);
      toast.error("Delete failed");
    } finally { setBusyId(null); }
  };

  // Super-admin only: force a profile into a verified / unverified state.
  // Optimistically patch the local list AND refresh so the change is visible
  // in the admin table immediately, matching the cascade on the public site.
  const applyVerification = async (pid, action) => {
    if (!window.confirm(action === "manual-verify"
      ? "Force-verify this profile without a video upload?"
      : "Remove verified status from this profile?")) return;
    setBusyId(pid);
    try {
      const { data } = await api.post(`/admin/profiles/${pid}/${action}`);
      const nextStatus = action === "manual-verify" ? "verified" : "unverified";
      setProfiles((ps) => ps.map((row) => row.id === pid
        ? { ...row, ...(data?.profile || {}),
            verification_status: nextStatus,
            verification_level: nextStatus === "verified" ? "verified" : "basic" }
        : row));
      toast.success(nextStatus === "verified" ? "Profile verified" : "Verification removed");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      console.warn("[AdminProfiles] applyVerification:", detail || e);
      toast.error(typeof detail === "string" ? detail : "Action failed");
    } finally { setBusyId(null); }
  };

  const verifStatus = (p) => p.verification_status
    || (p.verification?.manual_status === "approved" ? "verified"
        : p.verification?.manual_status === "pending" ? "pending"
        : p.verification?.manual_status === "rejected" ? "rejected"
        : "not_verified");

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Moderation</p>
          <h1 className="text-2xl sm:text-3xl font-serif text-stone-50 mt-1">Profiles</h1>
          <p className="text-sm text-stone-400 mt-1">{filtered.length} of {profiles.length}</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-stone-500 absolute left-3 top-3" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name / city / id"
            className="pl-9 pr-3 h-10 rounded-lg bg-stone-900/60 border border-stone-700 text-sm text-stone-100 w-full sm:w-72"
            data-testid="adm-profiles-search"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-xs text-rose-200 flex items-start gap-2" data-testid="adm-profiles-error">
          <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <p className="font-medium">Could not load profiles (HTTP {error.status ?? "?"})</p>
            <p className="text-rose-300/80 mt-0.5">{error.detail}</p>
          </div>
        </div>
      )}
      {loading && (
        <p className="mt-4 text-sm text-stone-400">Loading…</p>
      )}

      {/* Desktop table */}
      <div className="mt-5 rounded-xl border border-stone-800 bg-stone-950/50 overflow-hidden hidden lg:block">
        <table className="w-full text-sm" data-testid="adm-profiles-table">
          <thead className="text-[11px] uppercase tracking-wider text-stone-400 bg-stone-900/40">
            <tr>
              <th className="px-4 py-3 text-left">Profile</th>
              <th className="px-4 py-3 text-left">City</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Verification</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-800/70">
            {filtered.map(p => (
              <tr key={p.id} data-testid={`adm-profile-row-${p.id}`} className="hover:bg-stone-900/40">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={profileMainImage(p)} onError={onImageError} alt="" className="w-10 h-12 object-cover rounded" />
                    <div className="min-w-0">
                      <p className="text-stone-100 truncate">{p.display_name || p.name}</p>
                      <p className="text-[11px] text-stone-500 truncate">{p.id.slice(0, 8)}…</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-stone-300">{p.city || "—"}</td>
                <td className="px-4 py-3 text-stone-300">{p.is_independent ? "Independent" : "Agency"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] ${VERIF_TONE[verifStatus(p)]}`}>{verifStatus(p).replace("_", " ")}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] ${STATUS_TONE[p.status] || STATUS_TONE.pending}`}>{p.status || "pending"}</span>
                </td>
                <td className="px-4 py-3 text-[11px] text-stone-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <Link to={`/profile/${p.id}`} target="_blank" className="p-1.5 rounded hover:bg-stone-800 text-stone-300" title="View" data-testid={`adm-view-${p.id}`}><Eye className="w-3.5 h-3.5" /></Link>
                    {isSuper && (verifStatus(p) !== "verified" ? (
                      <button onClick={() => applyVerification(p.id, "manual-verify")} disabled={busyId === p.id} className="p-1.5 rounded hover:bg-emerald-500/10 text-emerald-300" title="Super-admin: manually verify" data-testid={`adm-manual-verify-${p.id}`}><ShieldCheck className="w-3.5 h-3.5" /></button>
                    ) : (
                      <button onClick={() => applyVerification(p.id, "unverify")} disabled={busyId === p.id} className="p-1.5 rounded hover:bg-rose-500/10 text-rose-300" title="Super-admin: remove verified status" data-testid={`adm-unverify-${p.id}`}><ShieldOff className="w-3.5 h-3.5" /></button>
                    ))}
                    <button onClick={() => setStatus(p.id, "active")}    disabled={busyId === p.id} className="p-1.5 rounded hover:bg-emerald-500/10 text-emerald-300" title="Approve" data-testid={`adm-approve-${p.id}`}><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setStatus(p.id, "rejected")}  disabled={busyId === p.id} className="p-1.5 rounded hover:bg-rose-500/10 text-rose-300" title="Reject" data-testid={`adm-reject-${p.id}`}><XCircle className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setStatus(p.id, "suspended")} disabled={busyId === p.id} className="p-1.5 rounded hover:bg-amber-500/10 text-amber-300" title="Suspend" data-testid={`adm-suspend-${p.id}`}><Ban className="w-3.5 h-3.5" /></button>
                    <button onClick={() => removeProfile(p.id)}          disabled={busyId === p.id} className="p-1.5 rounded hover:bg-rose-500/10 text-rose-300" title="Delete" data-testid={`adm-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-stone-500 text-sm">No profiles match the filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="mt-5 space-y-3 lg:hidden">
        {filtered.map(p => (
          <div key={p.id} className="rounded-xl border border-stone-800 bg-stone-950/50 p-3" data-testid={`adm-profile-card-${p.id}`}>
            <div className="flex items-start gap-3">
              <img src={profileMainImage(p)} onError={onImageError} alt="" className="w-14 h-16 object-cover rounded" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-stone-100 truncate">{p.display_name || p.name}</p>
                <p className="text-[11px] text-stone-500">{p.city} · {p.is_independent ? "Independent" : "Agency"}</p>
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] ${VERIF_TONE[verifStatus(p)]}`}>{verifStatus(p).replace("_", " ")}</span>
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] ${STATUS_TONE[p.status] || STATUS_TONE.pending}`}>{p.status || "pending"}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-1 mt-3 flex-wrap">
              <Link to={`/profile/${p.id}`} target="_blank" className="flex-1 text-center px-2 py-1.5 rounded border border-stone-700 text-stone-300 text-xs inline-flex items-center justify-center gap-1"><Eye className="w-3 h-3" /> View</Link>
              {isSuper && (verifStatus(p) !== "verified" ? (
                <button onClick={() => applyVerification(p.id, "manual-verify")} className="flex-1 px-2 py-1.5 rounded border border-emerald-500/50 text-emerald-300 text-xs inline-flex items-center justify-center gap-1" data-testid={`adm-manual-verify-m-${p.id}`}><ShieldCheck className="w-3 h-3" /> Verify</button>
              ) : (
                <button onClick={() => applyVerification(p.id, "unverify")} className="flex-1 px-2 py-1.5 rounded border border-rose-500/50 text-rose-300 text-xs inline-flex items-center justify-center gap-1" data-testid={`adm-unverify-m-${p.id}`}><ShieldOff className="w-3 h-3" /> Unverify</button>
              ))}
              <button onClick={() => setStatus(p.id, "active")}    className="flex-1 px-2 py-1.5 rounded border border-emerald-500/30 text-emerald-300 text-xs">Approve</button>
              <button onClick={() => setStatus(p.id, "rejected")}  className="flex-1 px-2 py-1.5 rounded border border-rose-500/30 text-rose-300 text-xs">Reject</button>
              <button onClick={() => setStatus(p.id, "suspended")} className="flex-1 px-2 py-1.5 rounded border border-amber-500/30 text-amber-300 text-xs">Suspend</button>
              <button onClick={() => removeProfile(p.id)}          className="flex-1 px-2 py-1.5 rounded border border-rose-500/50 text-rose-300 text-xs">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
